/**
 * src/components/mfa/EmailVerify.tsx
 * メールアドレスの確認（確認コードの送信 → 6桁コードの入力）の画面と状態。
 *   ・ステップ式の STEP1／ダイアログ／「メールアドレスを変更」で【同じ状態（useEmailVerify）と同じ画面（EmailVerifyPanel）】を使う。
 *     通信は既存の API（/auth/mfa/email/start → /auth/mfa/email/confirm）だけ。認証の条件はサーバーが決める（ここでは弱めない）。
 *   ・メールアドレスはログインID。いまと違うアドレスを確認済みにすると、次回からそのアドレスでログインする。
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { MfaApiError, mfaFetch } from '../../api/mfa'
import { codeProblem, loginIdWillChange, needsResend } from './setupFlow'

type Extra = { current_password?: string }
type WithReauth = <T>(fn: (extra: Extra) => Promise<T>) => Promise<T>
export type EmailMsg = { ok: boolean; text: string } | null

export type EmailVerifyState = {
  input: string; setInput: (v: string) => void
  pw: string; setPw: (v: string) => void
  stage: 'idle' | 'code'
  code: string; setCode: (v: string) => void
  masked: string | null
  msg: EmailMsg
  busy: 'send' | 'confirm' | null
  resendIn: number
  needResend: boolean
  start: (e?: React.FormEvent) => Promise<void>
  resend: () => Promise<void>
  confirm: (e: React.FormEvent) => Promise<void>
  restart: () => void
  reset: () => void
  /** 直近の確認で、ログインIDが変わった（変わった先のアドレス） */
  changedTo: string | null
}

export function useEmailVerify(opts: {
  withReauth: WithReauth
  /** 現在のログインID（メールアドレス） */
  currentEmail: string
  /** 確認が完了したとき（設定の読み直し・ユーザー情報の更新など） */
  onDone: () => Promise<void> | void
}): EmailVerifyState {
  const { withReauth, currentEmail, onDone } = opts
  const [input, setInput] = useState('')
  const [pw, setPw] = useState('')
  const [stage, setStage] = useState<'idle' | 'code'>('idle')
  const [code, setCode] = useState('')
  const [masked, setMasked] = useState<string | null>(null)
  const [msg, setMsg] = useState<EmailMsg>(null)
  const [busy, setBusy] = useState<'send' | 'confirm' | null>(null)
  const [resendIn, setResendIn] = useState(0)
  const [needResend, setNeedResend] = useState(false)
  const [changedTo, setChangedTo] = useState<string | null>(null)
  const sentTo = useRef('')

  useEffect(() => {
    if (resendIn <= 0) return undefined
    const t = setTimeout(() => setResendIn((n) => Math.max(0, n - 1)), 1000)
    return () => clearTimeout(t)
  }, [resendIn])

  const send = useCallback(async () => {
    setMsg(null); setBusy('send')
    try {
      const r = await withReauth((extra) => mfaFetch('/auth/mfa/email/start', 'POST', {
        email: input, current_password: extra.current_password ?? pw,
      }))
      sentTo.current = input.trim().toLowerCase()
      setMasked(r.email_masked ?? null)
      setStage('code'); setCode(''); setNeedResend(false)
      setResendIn(Number(r.resend_after_sec) || 60)
    } catch (err) {
      if (!(err instanceof MfaApiError && err.code === 'CANCELLED')) {
        setMsg({ ok: false, text: (err as Error).message })
        if (err instanceof MfaApiError && err.code === 'RESEND_TOO_SOON') {
          setResendIn(Number(err.data?.retry_after_sec) || 60)
        }
      }
    } finally { setBusy(null) }
  }, [withReauth, input, pw])

  const start = useCallback(async (e?: React.FormEvent) => {
    e?.preventDefault()
    await send()
  }, [send])

  const confirm = useCallback(async (e: React.FormEvent) => {
    e.preventDefault()
    setMsg(null); setBusy('confirm')
    try {
      const r = await mfaFetch('/auth/mfa/email/confirm', 'POST', { code })
      const changed = loginIdWillChange(currentEmail, sentTo.current || input)
      setChangedTo(changed ? String(r.email ?? sentTo.current) : null)
      setStage('idle'); setInput(''); setPw(''); setCode(''); setNeedResend(false)
      setMsg({ ok: true, text: 'メールアドレスを確認しました。' })
      await onDone()
    } catch (err) {
      const code2 = err instanceof MfaApiError ? err.code : undefined
      setMsg({ ok: false, text: (err as Error).message })
      setNeedResend(needsResend(codeProblem(code2)))
      setCode('')
    } finally { setBusy(null) }
  }, [code, currentEmail, input, onDone])

  const restart = useCallback(() => {
    setStage('idle'); setCode(''); setMsg(null); setNeedResend(false)
  }, [])

  const reset = useCallback(() => {
    setStage('idle'); setInput(''); setPw(''); setCode(''); setMasked(null); setMsg(null)
    setNeedResend(false); setResendIn(0); setChangedTo(null); setBusy(null)
  }, [])

  return {
    input, setInput, pw, setPw, stage, code, setCode, masked, msg, busy, resendIn, needResend,
    start, resend: send, confirm, restart, reset, changedTo,
  }
}

const field: React.CSSProperties = { fontSize: 16, width: '100%', boxSizing: 'border-box' }

function Notice({ tone, children }: { tone: 'ok' | 'ng' | 'warn' | 'info'; children: React.ReactNode }) {
  const c = {
    ok:   ['var(--green-light)', 'var(--green)'],
    ng:   ['var(--red-light)', 'var(--red)'],
    warn: ['var(--amber-light)', 'var(--amber)'],
    info: ['var(--paper-2)', 'var(--ink-2)'],
  }[tone]
  return (
    <div role={tone === 'ng' ? 'alert' : 'status'} style={{
      background: c[0], color: c[1], borderRadius: 8, padding: '10px 12px', fontSize: 13, lineHeight: 1.7, margin: '10px 0',
    }}>{children}</div>
  )
}

/** 確認コードの送信・入力フォーム（STEP1・ダイアログ・メール変更で共通） */
export function EmailVerifyPanel({ ev, currentEmail, placeholder, isMobile, mfaAvailable, submitLabel }: {
  ev: EmailVerifyState
  currentEmail: string
  placeholder: boolean
  isMobile: boolean
  mfaAvailable: boolean
  submitLabel?: string
}) {
  const h = isMobile ? 46 : undefined
  const willChange = loginIdWillChange(currentEmail, ev.input)

  if (ev.stage === 'code') {
    return (
      <form onSubmit={ev.confirm} aria-label="確認コードの入力">
        <div style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.7, marginBottom: 10 }}>
          {ev.masked ? <strong>{ev.masked}</strong> : 'メールアドレス'} に6桁のコードを送りました（有効5分）。メールを開いて、コードを入力してください。
        </div>
        <input aria-label="確認コード（6桁）" value={ev.code}
          onChange={(e) => ev.setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          inputMode="numeric" autoComplete="one-time-code" placeholder="123456" maxLength={6}
          style={{
            display: 'block', width: '100%', boxSizing: 'border-box', height: 52, fontSize: 22, letterSpacing: '0.4em',
            textAlign: 'center', fontFamily: 'var(--font-mono)', background: 'var(--paper)', color: 'var(--ink)',
            border: '1.5px solid var(--paper-3)', borderRadius: 8, outline: 'none',
          }} />
        {ev.msg && !ev.msg.ok && <Notice tone="ng">{ev.msg.text}</Notice>}
        {ev.needResend && (
          <Notice tone="warn">このコードは使えません。<strong>「コードを再送する」</strong>を押して、新しく届いたメールのコードを入力してください。</Notice>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
          <button type="submit" className="btn btn-primary" disabled={ev.busy !== null || ev.code.length !== 6}
            style={{ flex: isMobile ? 1 : undefined, height: isMobile ? 48 : undefined }}>
            {ev.busy === 'confirm' ? '確認中...' : '確認する'}
          </button>
          <button type="button" className="btn btn-outline" disabled={ev.busy !== null || ev.resendIn > 0}
            onClick={() => void ev.resend()}>
            {ev.resendIn > 0 ? `コードを再送する（${ev.resendIn}秒後）` : 'コードを再送する'}
          </button>
          <button type="button" className="btn btn-ghost" disabled={ev.busy !== null} onClick={ev.restart}>アドレスを入れ直す</button>
        </div>
        <div style={{ fontSize: 12, color: 'var(--ink-3)', lineHeight: 1.7, marginTop: 10 }}>
          届かないときは、まず迷惑メールフォルダを確認してください。再送すると前のコードは使えなくなります（最後に届いたものを入力）。
        </div>
      </form>
    )
  }

  return (
    <form onSubmit={(e) => void ev.start(e)} aria-label="メールアドレスの確認">
      {placeholder && (
        <Notice tone="warn">
          いま登録されているメールアドレスは<strong>仮のもの</strong>です。<strong>ご自分が受信できるメールアドレス</strong>を入力してください。
        </Notice>
      )}
      <div className="form-group" style={{ marginBottom: 12 }}>
        <label>確認するメールアドレス</label>
        <input aria-label="確認するメールアドレス" type="email" required value={ev.input} onChange={(e) => ev.setInput(e.target.value)}
          placeholder="you@example.com" autoComplete="email" style={{ ...field, height: h }} />
      </div>
      {willChange ? (
        <Notice tone="warn">
          <strong>このアドレスが、新しいログインIDになります。</strong>次回から、このアドレスと今までのパスワードでログインします。
          他の端末のログインは外れます。
        </Notice>
      ) : (
        <div style={{ fontSize: 12, color: 'var(--ink-3)', lineHeight: 1.7, marginBottom: 10 }}>
          メールアドレスはログインIDです。いまと同じアドレスなら、ログインIDは変わりません。
        </div>
      )}
      <div className="form-group" style={{ marginBottom: 12 }}>
        <label>現在のパスワード（本人確認）</label>
        <input aria-label="現在のパスワード（メール確認用）" type="password" required value={ev.pw} onChange={(e) => ev.setPw(e.target.value)}
          autoComplete="current-password" style={{ ...field, height: h }} />
      </div>
      {ev.msg && <Notice tone={ev.msg.ok ? 'ok' : 'ng'}>{ev.msg.text}</Notice>}
      <button type="submit" className="btn btn-primary" disabled={ev.busy !== null || !mfaAvailable || ev.resendIn > 0}
        style={{ width: isMobile ? '100%' : undefined, height: isMobile ? 48 : undefined }}>
        {ev.busy === 'send' ? '送信中...' : ev.resendIn > 0 ? `あと${ev.resendIn}秒で送れます` : (submitLabel ?? '確認コードを送る')}
      </button>
      {!mfaAvailable && (
        <div style={{ fontSize: 12, color: 'var(--amber)', marginTop: 8 }}>※ メールによる確認は現在ご利用いただけません。管理者に連絡してください。</div>
      )}
    </form>
  )
}

/** 方式を選んだのにメールが未確認のときに出すダイアログ。確認が終わるか、キャンセルするまで開いたまま */
export function EmailConfirmDialog({ ev, currentEmail, placeholder, isMobile, mfaAvailable, reason, onCancel }: {
  ev: EmailVerifyState
  currentEmail: string
  placeholder: boolean
  isMobile: boolean
  mfaAvailable: boolean
  /** 例: 「パスキーを登録する」 */
  reason: string
  onCancel: () => void
}) {
  return (
    <div className="overlay" style={{ zIndex: 1250 }}>
      <div className="modal" style={{ maxWidth: 460 }} role="dialog" aria-modal="true" aria-label="先にメールアドレスを確認します">
        <div className="modal-header">
          <div className="modal-title">✉️ 先に、メールアドレスの確認が必要です</div>
          <button className="btn btn-ghost btn-sm" onClick={onCancel} aria-label="キャンセルして戻る">✕</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.7, marginTop: 0 }}>
            「{reason}」の前に、ご自分のメールアドレスを確認します（通知メールが、別の人に届かないようにするためです）。
            確認が終わると、<strong>自動で「{reason}」に進みます</strong>。
          </p>
          <EmailVerifyPanel ev={ev} currentEmail={currentEmail} placeholder={placeholder} isMobile={isMobile} mfaAvailable={mfaAvailable} />
          <button type="button" className="btn btn-ghost" style={{ marginTop: 12 }} onClick={onCancel}>キャンセルして戻る</button>
        </div>
      </div>
    </div>
  )
}
